/**
 * To-Do Progress Widget
 *
 * Shows task completion progress with pending items list
 * and quick complete functionality.
 *
 * @module shared/components/widgets/TodoProgressWidget
 */

import { isAfter, parseISO, startOfDay } from 'date-fns';
import {
  CheckSquare,
  Circle,
  CheckCircle2,
  AlertCircle,
  ListTodo,
} from 'lucide-react';
import { useMemo } from 'react';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/shared/components/ui/card';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Progress } from '@/shared/components/ui/progress';
import { TruncatedText } from '@/shared/components/ui/truncated-text';
import { useAppNavigation } from '@/shared/hooks/useAppNavigation';
import { cn } from '@/shared/lib/utils';
import { BadgeVariant } from '@/shared/types/badge.types';
import { ButtonVariant, ButtonSize } from '@/shared/types/button.types';
import { ProgressVariant, ProgressSize } from '@/shared/types/progress.types';

// ============================================
// Types
// ============================================

type WidgetTaskPriority = 'high' | 'medium' | 'low';
type WidgetTaskStatus = 'pending' | 'in-progress' | 'completed' | 'cancelled';

/** Minimal task shape consumed by the progress widget. */
interface WidgetTask {
  readonly id: string;
  readonly title: string;
  readonly status: WidgetTaskStatus;
  readonly priority: WidgetTaskPriority;
  readonly listId: string;
  readonly dueDate?: string | null;
  readonly emoji?: string | null;
}

/** Minimal task-list shape used to resolve list titles. */
interface WidgetTaskList {
  readonly id: string;
  readonly title: string;
}

/** Bulgarian labels for task priorities. */
const PRIORITY_LABELS: Readonly<Record<WidgetTaskPriority, string>> = {
  high: 'Висок',
  medium: 'Среден',
  low: 'Нисък',
};

interface TodoProgressWidgetProps {
  /** Tasks to display (loaded by a container) */
  readonly tasks?: readonly WidgetTask[];
  /** Task lists used to resolve list titles */
  readonly taskLists?: readonly WidgetTaskList[];
  /** Toggle a task's completion status */
  readonly onToggleTask?: (taskId: string) => void;
  /** Number of pending items to show */
  readonly pendingCount?: number;
  /** Additional CSS classes */
  readonly className?: string;
  /** Show header */
  readonly showHeader?: boolean;
  /** Show pending items list */
  readonly showPendingList?: boolean;
}

// ============================================
// Priority Badge Colors
// ============================================

const PRIORITY_COLORS: Record<WidgetTaskPriority, string> = {
  high: 'bg-red-500/10 text-red-600 dark:text-red-400',
  medium: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
  low: 'bg-green-500/10 text-green-600 dark:text-green-400',
};

// ============================================
// Main Widget Component
// ============================================

export function TodoProgressWidget({
  tasks = [],
  taskLists = [],
  onToggleTask,
  pendingCount = 5,
  className,
  showHeader = true,
  showPendingList = true,
}: TodoProgressWidgetProps) {
  const { goToTodoList } = useAppNavigation();

  // Calculate stats
  const stats = useMemo(() => {
    const total = tasks.length;
    const completed = tasks.filter((t) => t.status === 'completed').length;
    const pending = total - completed;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    // Get overdue count
    const today = startOfDay(new Date());
    const overdue = tasks.filter(
      (t) =>
        t.status !== 'completed' &&
        t.dueDate &&
        isAfter(today, parseISO(t.dueDate))
    ).length;

    return { total, completed, pending, percentage, overdue };
  }, [tasks]);

  // Get pending tasks (sorted by priority and due date)
  const pendingTasks = useMemo(() => {
    const priorityOrder: Record<WidgetTaskPriority, number> = {
      high: 0,
      medium: 1,
      low: 2,
    };

    return tasks
      .filter((t) => t.status !== 'completed')
      .sort((a, b) => {
        // First by priority
        const priorityDiff =
          priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;

        // Then by due date
        if (a.dueDate && b.dueDate) {
          return parseISO(a.dueDate).getTime() - parseISO(b.dueDate).getTime();
        }
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;

        return 0;
      })
      .slice(0, pendingCount);
  }, [tasks, pendingCount]);

  // Get list title for a task
  const getListTitle = (listId: string): string => {
    const list = taskLists.find((l) => l.id === listId);
    return list?.title ?? 'Без списък';
  };

  // Handle task toggle
  const handleToggle = (taskId: string) => {
    onToggleTask?.(taskId);
  };

  // Navigate to todo page
  const handleViewAll = () => {
    goToTodoList();
  };

  // Progress bar variant based on completion
  const getProgressVariant = (percentage: number): ProgressVariant => {
    if (percentage >= 80) return ProgressVariant.Success;
    if (percentage >= 50) return ProgressVariant.Warning;
    return ProgressVariant.Error;
  };

  return (
    <Card className={cn('overflow-hidden', className)}>
      {showHeader && (
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-1.5 text-sm">
              <CheckSquare className="size-4" />
              Задачи
            </CardTitle>
            <Button
              variant={ButtonVariant.Ghost}
              size={ButtonSize.Small}
              className="h-7 px-2 text-xs"
              onClick={handleViewAll}
            >
              Виж всички
            </Button>
          </div>
        </CardHeader>
      )}

      <CardContent className={cn('p-4', showHeader && 'pt-0')}>
        {/* Progress stats */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <CheckCircle2 className="size-3.5 text-green-500" />
              {stats.completed} от {stats.total} завършени
            </span>
            <span className="font-medium">{stats.percentage}%</span>
          </div>

          <Progress
            value={stats.percentage}
            size={ProgressSize.Default}
            variant={getProgressVariant(stats.percentage)}
          />

          {/* Quick stats */}
          <div className="flex items-center gap-4 pt-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Circle className="size-3" />
              {stats.pending} чакащи
            </span>
            {stats.overdue > 0 && (
              <span className="flex items-center gap-1 text-destructive">
                <AlertCircle className="size-3" />
                {stats.overdue} просрочени
              </span>
            )}
          </div>
        </div>

        {/* Pending items list */}
        {showPendingList && pendingTasks.length > 0 && (
          <div className="mt-4 border-t border-border pt-4">
            <div className="space-y-2">
              {pendingTasks.map((task) => (
                <div
                  key={task.id}
                  className={cn(
                    'flex items-start gap-2 p-2 -mx-2 rounded-lg',
                    'hover:bg-accent/50 transition-colors'
                  )}
                >
                  <Checkbox
                    checked={task.status === 'completed'}
                    onCheckedChange={() => handleToggle(task.id)}
                    className="mt-0.5"
                    aria-label={`Маркирай "${task.title}" като завършена`}
                  />

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {task.emoji && (
                        <span className="text-sm" aria-hidden="true">
                          {task.emoji}
                        </span>
                      )}
                      <TruncatedText className="text-sm font-medium">
                        {task.title}
                      </TruncatedText>
                    </div>

                    <div className="mt-0.5 flex items-center gap-2">
                      <Badge
                        variant={BadgeVariant.Outline}
                        className={cn(
                          'text-xs px-1.5 py-0 h-5',
                          PRIORITY_COLORS[task.priority]
                        )}
                      >
                        {PRIORITY_LABELS[task.priority]}
                      </Badge>
                      <TruncatedText className="text-xs text-muted-foreground">
                        {getListTitle(task.listId)}
                      </TruncatedText>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {showPendingList && pendingTasks.length === 0 && stats.total === 0 && (
          <div className="mt-4 flex flex-col items-center justify-center border-t border-border py-6 pt-4 text-center">
            <ListTodo className="mb-2 size-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Няма задачи</p>
            <Button
              variant={ButtonVariant.Link}
              size={ButtonSize.Small}
              className="mt-1 text-xs"
              onClick={handleViewAll}
            >
              Добави задача
            </Button>
          </div>
        )}

        {/* All done state */}
        {showPendingList && pendingTasks.length === 0 && stats.total > 0 && (
          <div className="mt-4 flex flex-col items-center justify-center border-t border-border py-4 text-center">
            <CheckCircle2 className="mb-2 size-8 text-green-500" />
            <p className="text-sm font-medium text-green-600 dark:text-green-400">
              Браво! Всички задачи са завършени! 🎉
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Default export for easier imports
export default TodoProgressWidget;
